import { FormPercentageInput } from '@exam-management/components';
import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigStatus } from '@exam-management/graphql';
import { Table, TableBody, TableCell, TableContainer, TableRow, Text } from '@shared/components';
import { type TableHeaderRow, TableHeader } from '@shared/table-generator';

import { useQConfigSettingsFormContext } from '../../providers';
import { calculateScore } from '../../utils';

interface OverallScoreTableProps {
  disabled: boolean;
}

const headers: TableHeaderRow[] = [
  {
    children: [
      {
        id: 'overall-score-criteria',
        children: 'คะแนนรวมที่ต้องผ่าน',
        align: 'center',
        colSpan: 2,
      },
    ],
  },
  {
    children: [
      {
        id: 'overall-score-percentage-input',
        children: 'เปอร์เซ็นต์',
        align: 'center',
        rowSpan: 1,
      },
      {
        id: 'calculated-overall-score-criteria',
        children: 'คะแนน',
        align: 'center',
        rowSpan: 1,
      },
    ],
  },
];

export default function OverallScoreTable(props: Readonly<OverallScoreTableProps>) {
  const { disabled: isDisabled } = props;
  const { qConfigData } = useQConfig();
  const { fullScore, passingCriteria } = qConfigData;
  const formContext = useQConfigSettingsFormContext();

  const { watch } = formContext;
  const percentage = watch('passingCriteria.overall') ?? passingCriteria?.value ?? 0;
  const calculatedScore = calculateScore(fullScore, percentage);

  const handleChange = () => {
    formContext.trigger(['passingCriteria.structures']);
  };

  return (
    <TableContainer
      sx={{
        borderRadius: (theme) => theme.shape.rounded(2),
        border: '1px solid',
        borderColor: (theme) => theme.palette.grey[300],
        width: '304px',
      }}
    >
      <Table sx={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <TableHeader
          columns={headers}
          slotProps={{
            tableHeaderRow: {
              sx: (theme) => ({
                ':first-of-type': {
                  th: {
                    borderRadius: theme.shape.rounded(0),
                  },
                },
                ':nth-of-type(2)': {
                  th: {
                    borderRadius: theme.shape.rounded(0),
                  },
                },
              }),
            },
            tableHeaderCell: {
              sx: (theme) => ({
                ...theme.typography.body1Semibold,
                backgroundColor: theme.palette.grey[100],
                borderRadius: theme.shape.rounded(2),
                borderBottom: `1px solid ${theme.palette.grey[300]}`,
                px: theme.spacing(3),
                py: theme.spacing(2),
                ':nth-of-type(2)': {
                  borderLeft: `1px solid ${theme.palette.grey[300]}`,
                },
              }),
            },
          }}
        />
        <TableBody>
          <TableRow
            sx={(theme) => ({
              '& > :not(td:first-of-type)': {
                borderLeft: `1px solid ${theme.palette.grey[300]}`,
              },
              '& > td': {
                borderColor: theme.palette.grey[300],
                borderRadius: theme.shape.rounded(2),
                borderBottom: 0,
                p: theme.spacing(3),
              },
            })}
          >
            <TableCell align='center'>
              <FormPercentageInput
                name='passingCriteria.overall'
                onChange={handleChange}
                disabled={qConfigData.status !== QConfigStatus.Draft || isDisabled}
              />
            </TableCell>
            <TableCell align='center'>
              <Text variant='body1Semibold'>{calculatedScore}</Text>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
